import {ScriptOperators} from '@/components/project/ScriptOperators'
import { ProductionPipelinePanel } from '@/components/project/ProductionPipelinePanel'
import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ChevronLeft, Loader2, Save } from 'lucide-react'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import { ApiError, friendlyMessage } from '@/api/http'
import { ContentModeSwitch } from '@/components/ContentModeSwitch'
import { chapterApi } from '@/api/chapterApi'
import { projectApi } from '@/api/projectApi'
import { workbenchApi, type WorkstationConfigResponse } from '@/api/workbenchApi'
import { WorkbenchShell } from '@/components/workbench/WorkbenchShell'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import {
  formatConfigForEditor,
  isWorkbenchStage,
  panelImageConfigFromUnknown,
  panelImageConfigToJSON,
  parseEditorConfig,
  type PanelImageWorkbenchConfig,
  screenplayConfigFromUnknown,
  screenplayConfigToJSON,
  storyboardConfigFromUnknown,
  storyboardConfigToJSON,
  type ScreenplayWorkbenchConfig,
  type StoryboardWorkbenchConfig,
  type WorkbenchStage,
} from '@/lib/workbenchConfig'
import { cn } from '@/lib/utils'
import { useTranslation } from 'react-i18next'

type ConfigScope = 'project' | 'chapter'

const SELECT_DEFAULT = '__default__'

function emptyConfigResponse(projectId: string, wsType: WorkbenchStage, chapterId: string | null): WorkstationConfigResponse {
  return {
    project_id: projectId,
    ws_type: wsType,
    chapter_id: chapterId,
    config_json: {},
  }
}

async function getOptionalConfig(projectId: string, wsType: WorkbenchStage, chapterId?: string) {
  try {
    return await workbenchApi.getConfig(projectId, wsType, chapterId)
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) return emptyConfigResponse(projectId, wsType, chapterId ?? null)
    throw error
  }
}

export default function WorkbenchPage() {
  const params = useParams()
  const projectId = params.id ?? ''
  const chapterId = params.cid ?? ''
  const stageParam = params.stage

  if (!projectId || !chapterId || !isWorkbenchStage(stageParam)) {
    return <Navigate to={projectId ? `/projects/${encodeURIComponent(projectId)}` : '/projects'} replace />
  }

  return <WorkbenchPageContent projectId={projectId} chapterId={chapterId} stage={stageParam} />
}

function WorkbenchPageContent({ projectId, chapterId, stage }: { projectId: string; chapterId: string; stage: WorkbenchStage }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [scope, setScope] = useState<ConfigScope>('chapter')
  const [configText, setConfigText] = useState('{}')
  const scopedChapterId = scope === 'chapter' ? chapterId : undefined

  const projectQuery = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => projectApi.get(projectId),
  })
  const chapterQuery = useQuery({
    queryKey: ['chapter', chapterId],
    queryFn: () => chapterApi.get(chapterId),
  })
  const rawConfigQuery = useQuery({
    queryKey: ['workbench-config', projectId, stage, scopedChapterId ?? 'project'],
    queryFn: () => getOptionalConfig(projectId, stage, scopedChapterId),
  })
  const resolvedConfigQuery = useQuery({
    queryKey: ['workbench-config-resolved', projectId, stage, chapterId],
    queryFn: () => workbenchApi.getResolvedConfig(projectId, stage, chapterId),
  })
  const optionsQuery = useQuery({
    queryKey: ['workbench-options', projectId],
    queryFn: () => workbenchApi.getOptions(projectId),
  })

  useEffect(() => {
    if (!rawConfigQuery.data) return
    setConfigText(formatConfigForEditor(rawConfigQuery.data.config_json))
  }, [rawConfigQuery.data])

  const parsed = useMemo(() => parseEditorConfig(configText), [configText])
  const screenplayConfig = useMemo(() => screenplayConfigFromUnknown(parsed.ok ? parsed.value : {}), [parsed])
  const storyboardConfig = useMemo(() => storyboardConfigFromUnknown(parsed.ok ? parsed.value : {}), [parsed])
  const panelImageConfig = useMemo(() => panelImageConfigFromUnknown(parsed.ok ? parsed.value : {}), [parsed])
  const setScreenplayConfig = (next: ScreenplayWorkbenchConfig) => {
    setConfigText(formatConfigForEditor(screenplayConfigToJSON(next)))
  }
  const setStoryboardConfig = (next: StoryboardWorkbenchConfig) => {
    setConfigText(formatConfigForEditor(storyboardConfigToJSON(next)))
  }
  const setPanelImageConfig = (next: PanelImageWorkbenchConfig) => {
    setConfigText(formatConfigForEditor(panelImageConfigToJSON(next)))
  }
  const saveMutation = useMutation({
    mutationFn: async () => {
      const current =
        stage === 'screenplay'
          ? { ok: true as const, value: screenplayConfigToJSON(screenplayConfig) }
          : stage === 'storyboard'
            ? { ok: true as const, value: storyboardConfigToJSON(storyboardConfig) }
            : stage === 'panel_image'
              ? { ok: true as const, value: panelImageConfigToJSON(panelImageConfig) }
              : parseEditorConfig(configText)
      if (!current.ok) throw new Error(current.message)
      return workbenchApi.putConfig(projectId, {
        ws_type: stage,
        chapter_id: scopedChapterId,
        config_json: current.value,
      })
    },
    onSuccess: async () => {
      toast.success(t('workbench.toast.saved'))
      await queryClient.invalidateQueries({ queryKey: ['workbench-config', projectId, stage] })
      await queryClient.invalidateQueries({ queryKey: ['workbench-config-resolved', projectId, stage, chapterId] })
    },
    onError: (error) => {
      toast.error(friendlyMessage(error, t('workbench.toast.saveFailed')))
    },
  })

  const chapterLabel = chapterQuery.data
    ? t('workbench.chapterLabel', { number: chapterQuery.data.chapter_number, title: chapterQuery.data.title })
    : chapterQuery.isLoading
      ? t('common.loading')
      : t('workbench.chapterUnavailable')
  const stationName = t(`workbench.stations.${stage}`)
  // 章节是辅助信息(提供标签 + 章节级覆盖); 配置本身是项目级, 章节加载失败应优雅降级,
  // 不把辅助的章节错误当致命错误阻塞整个工作台(否则非法/不存在章节会抛原始 forbidden)。
  const loading = projectQuery.isLoading || rawConfigQuery.isLoading
  const richConfigStage = stage === 'screenplay' || stage === 'storyboard' || stage === 'panel_image'
  const loadError = projectQuery.error || rawConfigQuery.error || resolvedConfigQuery.error || (richConfigStage ? optionsQuery.error : null)

  return (
    <WorkbenchShell
      stationName={stationName}
      chapterLabel={chapterLabel}
      versionLabel={t('workbench.versionCurrent')}
      configPanel={(
        <div className="flex h-full min-h-0 flex-col gap-4">
          <Button type="button" variant="ghost" size="sm" className="w-fit px-2" onClick={() => navigate(`/projects/${projectId}`)}>
            <ChevronLeft className="size-4" />
            {t('workbench.backToProject')}
          </Button>
          {/* 全局创作档位, 与项目页标题行同一个状态 */}
          <ContentModeSwitch />
          <div className="grid grid-cols-2 rounded-md border p-1">
            <Button
              type="button"
              variant={scope === 'project' ? 'secondary' : 'ghost'}
              size="sm"
              className="rounded-sm"
              onClick={() => setScope('project')}
            >
              {t('workbench.scope.project')}
            </Button>
            <Button
              type="button"
              variant={scope === 'chapter' ? 'secondary' : 'ghost'}
              size="sm"
              className="rounded-sm"
              onClick={() => setScope('chapter')}
            >
              {t('workbench.scope.chapter')}
            </Button>
          </div>
          {loadError ? (
            <Alert variant="destructive">
              <AlertDescription>{friendlyMessage(loadError, t('workbench.loadFailed'))}</AlertDescription>
            </Alert>
          ) : null}
          {stage === 'screenplay' ? (
            <ScreenplayConfigPanel
              config={screenplayConfig}
              disabled={loading}
              loading={loading || optionsQuery.isLoading}
              stylePresets={optionsQuery.data?.screenplay.style_presets ?? []}
              lengthOptions={optionsQuery.data?.screenplay.length_options ?? []}
              dialogueDensityOptions={optionsQuery.data?.screenplay.dialogue_density_options ?? []}
              povOptions={optionsQuery.data?.screenplay.pov_options ?? []}
              onChange={setScreenplayConfig}
            />
          ) : stage === 'storyboard' ? (
            <StoryboardConfigPanel
              config={storyboardConfig}
              disabled={loading}
              loading={loading || optionsQuery.isLoading}
              shotDensityOptions={optionsQuery.data?.storyboard.shot_density_options ?? []}
              preferredShotTypes={optionsQuery.data?.storyboard.preferred_shot_types ?? []}
              cameraLanguages={optionsQuery.data?.storyboard.camera_languages ?? []}
              styleLocks={optionsQuery.data?.storyboard.style_locks ?? []}
              onChange={setStoryboardConfig}
            />
          ) : stage === 'panel_image' ? (
            <PanelImageConfigPanel
              config={panelImageConfig}
              disabled={loading}
              loading={loading || optionsQuery.isLoading}
              models={optionsQuery.data?.panel_image.models ?? []}
              sizePresets={optionsQuery.data?.panel_image.size_presets ?? []}
              stylePresets={optionsQuery.data?.panel_image.style_presets ?? []}
              onChange={setPanelImageConfig}
            />
          ) : (
            <div className="flex min-h-0 flex-1 flex-col gap-2">
              <Label htmlFor="workbench-config-json">{t('workbench.configJson')}</Label>
              {loading ? (
                <Skeleton className="min-h-[28rem] flex-1 rounded-md" />
              ) : (
                <textarea
                  id="workbench-config-json"
                  value={configText}
                  onChange={(event) => setConfigText(event.target.value)}
                  spellCheck={false}
                  className={cn(
                    'min-h-[28rem] flex-1 resize-none rounded-md border bg-background px-3 py-2 font-mono text-sm outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50',
                    !parsed.ok && 'border-destructive focus-visible:ring-destructive/20',
                  )}
                />
              )}
              {!parsed.ok ? <p className="text-sm text-destructive">{parsed.message}</p> : null}
            </div>
          )}
          <Button
            type="button"
            disabled={(!richConfigStage && !parsed.ok) || saveMutation.isPending || loading}
            onClick={() => saveMutation.mutate()}
          >
            {saveMutation.isPending ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />}
            {t('workbench.saveConfig')}
          </Button>
        </div>
      )}
      previewPanel={(
        <div className="h-full overflow-auto">
          {stage === 'screenplay' && <ScriptOperators key={chapterId} chapterId={chapterId} />}
          {chapterQuery.data && projectQuery.data ? (
            stage === 'chapter_text' ? <article className="whitespace-pre-wrap p-4">{chapterQuery.data.content || t('workbench.chapterUnavailable')}</article> :
            <ProductionPipelinePanel project={projectQuery.data} chapter={chapterQuery.data} onlyStage={stage} />
          ) : <p className="p-4 text-sm text-muted-foreground">{chapterLabel}</p>}
        </div>
      )}
      versionPanel={(
        <div className="space-y-3 p-4">
          <p className="text-sm text-muted-foreground">{t('workbench.pipelineHint')}</p>
          <Button onClick={() => navigate(`/projects/${projectId}?tab=pipeline`)}>{t('workbench.openPipeline')}</Button>
        </div>
      )}

    />
  )
}

function ScreenplayConfigPanel({
  config,
  disabled,
  loading,
  stylePresets,
  lengthOptions,
  dialogueDensityOptions,
  povOptions,
  onChange,
}: {
  config: ScreenplayWorkbenchConfig
  disabled: boolean
  loading: boolean
  stylePresets: string[]
  lengthOptions: string[]
  dialogueDensityOptions: string[]
  povOptions: string[]
  onChange: (next: ScreenplayWorkbenchConfig) => void
}) {
  const { t } = useTranslation()
  if (loading) return <Skeleton className="min-h-[28rem] flex-1 rounded-md" />

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      <PresetInput
        label={t('workbench.screenplay.style')}
        value={config.style}
        options={stylePresets}
        placeholder={t('workbench.screenplay.customStylePlaceholder')}
        disabled={disabled}
        defaultLabel={t('workbench.configDefault')}
        onChange={(style) => onChange({ ...config, style })}
      />
      <OptionSelect
        label={t('workbench.screenplay.length')}
        value={config.length}
        options={lengthOptions}
        disabled={disabled}
        defaultLabel={t('workbench.configDefault')}
        onChange={(length) => onChange({ ...config, length })}
      />
      <OptionSelect
        label={t('workbench.screenplay.dialogueDensity')}
        value={config.dialogueDensity}
        options={dialogueDensityOptions}
        disabled={disabled}
        defaultLabel={t('workbench.configDefault')}
        onChange={(dialogueDensity) => onChange({ ...config, dialogueDensity })}
      />
      <OptionSelect
        label={t('workbench.screenplay.pov')}
        value={config.pov}
        options={povOptions}
        disabled={disabled}
        defaultLabel={t('workbench.configDefault')}
        onChange={(pov) => onChange({ ...config, pov })}
      />
    </div>
  )
}

function StoryboardConfigPanel({
  config,
  disabled,
  loading,
  shotDensityOptions,
  preferredShotTypes,
  cameraLanguages,
  styleLocks,
  onChange,
}: {
  config: StoryboardWorkbenchConfig
  disabled: boolean
  loading: boolean
  shotDensityOptions: string[]
  preferredShotTypes: string[]
  cameraLanguages: string[]
  styleLocks: string[]
  onChange: (next: StoryboardWorkbenchConfig) => void
}) {
  const { t } = useTranslation()
  if (loading) return <Skeleton className="min-h-[28rem] flex-1 rounded-md" />

  const toggleShotType = (shotType: string) => {
    const exists = config.preferredShotTypes.includes(shotType)
    onChange({
      ...config,
      preferredShotTypes: exists
        ? config.preferredShotTypes.filter((value) => value !== shotType)
        : [...config.preferredShotTypes, shotType],
    })
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      <OptionSelect
        label={t('workbench.storyboard.shotDensity')}
        value={config.shotDensity}
        options={shotDensityOptions}
        disabled={disabled}
        defaultLabel={t('workbench.configDefault')}
        onChange={(shotDensity) => onChange({ ...config, shotDensity })}
      />
      <div className="space-y-2">
        <Label>{t('workbench.storyboard.preferredShotTypes')}</Label>
        <div className="grid grid-cols-1 gap-2">
          {preferredShotTypes.map((shotType) => (
            <label key={shotType} className="flex min-h-9 items-center gap-2 rounded-md border px-3 py-2 text-sm">
              <input
                type="checkbox"
                className="size-4"
                checked={config.preferredShotTypes.includes(shotType)}
                disabled={disabled}
                onChange={() => toggleShotType(shotType)}
              />
              <span className="break-all">{shotType}</span>
            </label>
          ))}
        </div>
      </div>
      <PresetInput
        label={t('workbench.storyboard.cameraLanguage')}
        value={config.cameraLanguage}
        options={cameraLanguages}
        placeholder={t('workbench.storyboard.customCameraLanguagePlaceholder')}
        disabled={disabled}
        defaultLabel={t('workbench.configDefault')}
        onChange={(cameraLanguage) => onChange({ ...config, cameraLanguage })}
      />
      <PresetInput
        label={t('workbench.storyboard.styleLock')}
        value={config.styleLock}
        options={styleLocks}
        placeholder={t('workbench.storyboard.customStyleLockPlaceholder')}
        disabled={disabled}
        defaultLabel={t('workbench.configDefault')}
        onChange={(styleLock) => onChange({ ...config, styleLock })}
      />
      <label className="flex min-h-10 items-center justify-between gap-3 rounded-md border px-3 py-2 text-sm">
        <span>{t('workbench.storyboard.sketchReviewEnabled')}</span>
        <input
          type="checkbox"
          className="size-4"
          checked={config.sketchReviewEnabled}
          disabled={disabled}
          onChange={(event) => onChange({ ...config, sketchReviewEnabled: event.target.checked })}
        />
      </label>
    </div>
  )
}

function PanelImageConfigPanel({
  config,
  disabled,
  loading,
  models,
  sizePresets,
  stylePresets,
  onChange,
}: {
  config: PanelImageWorkbenchConfig
  disabled: boolean
  loading: boolean
  models: string[]
  sizePresets: string[]
  stylePresets: string[]
  onChange: (next: PanelImageWorkbenchConfig) => void
}) {
  const { t } = useTranslation()
  const modelValue = config.model || SELECT_DEFAULT
  const sizeValue = config.size || SELECT_DEFAULT
  const styleValue = config.stylePreset || SELECT_DEFAULT
  const updateStyleLock = (field: keyof PanelImageWorkbenchConfig['styleLock'], value: string) => {
    onChange({ ...config, styleLock: { ...config.styleLock, [field]: value } })
  }

  if (loading) {
    return <Skeleton className="min-h-[28rem] flex-1 rounded-md" />
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      <div className="space-y-2">
        <Label>{t('workbench.panelImage.model')}</Label>
        <Select
          value={modelValue}
          disabled={disabled || models.length === 0}
          onValueChange={(value) => onChange({ ...config, model: value === SELECT_DEFAULT ? '' : value })}
        >
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={SELECT_DEFAULT}>{t('workbench.panelImage.configDefault')}</SelectItem>
            {models.map((model) => (
              <SelectItem key={model} value={model}>{model}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label>{t('workbench.panelImage.size')}</Label>
        <Select
          value={sizeValue}
          disabled={disabled || sizePresets.length === 0}
          onValueChange={(value) => onChange({ ...config, size: value === SELECT_DEFAULT ? '' : value })}
        >
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={SELECT_DEFAULT}>{t('workbench.panelImage.configDefault')}</SelectItem>
            {sizePresets.map((size) => (
              <SelectItem key={size} value={size}>{size}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label>{t('workbench.panelImage.seedStrategy')}</Label>
        <Select
          value={config.seedStrategy}
          disabled={disabled}
          onValueChange={(value) => onChange({
            ...config,
            seedStrategy: value === 'fixed' ? 'fixed' : 'deterministic',
            seed: value === 'fixed' ? config.seed : '',
          })}
        >
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="deterministic">{t('workbench.panelImage.seedDeterministic')}</SelectItem>
            <SelectItem value="fixed">{t('workbench.panelImage.seedFixed')}</SelectItem>
          </SelectContent>
        </Select>
        {config.seedStrategy === 'fixed' ? (
          <Input
            type="number"
            inputMode="numeric"
            value={config.seed}
            disabled={disabled}
            placeholder={t('workbench.panelImage.seedPlaceholder')}
            onChange={(event) => onChange({ ...config, seed: event.target.value })}
          />
        ) : null}
      </div>
      <div className="space-y-2">
        <Label>{t('workbench.panelImage.stylePreset')}</Label>
        <Select
          value={styleValue}
          disabled={disabled || stylePresets.length === 0}
          onValueChange={(value) => onChange({ ...config, stylePreset: value === SELECT_DEFAULT ? '' : value })}
        >
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={SELECT_DEFAULT}>{t('workbench.panelImage.configDefault')}</SelectItem>
            {stylePresets.map((style) => (
              <SelectItem key={style} value={style}>{style}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-2">
          <Label>{t('workbench.panelImage.styleLockArtStyle')}</Label>
          <Input
            value={config.styleLock.artStyle}
            disabled={disabled}
            placeholder={t('workbench.panelImage.styleLockArtStylePlaceholder')}
            onChange={(event) => updateStyleLock('artStyle', event.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label>{t('workbench.panelImage.styleLockPalette')}</Label>
          <Input
            value={config.styleLock.palette}
            disabled={disabled}
            placeholder={t('workbench.panelImage.styleLockPalettePlaceholder')}
            onChange={(event) => updateStyleLock('palette', event.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label>{t('workbench.panelImage.styleLockQuality')}</Label>
          <Input
            value={config.styleLock.quality}
            disabled={disabled}
            placeholder={t('workbench.panelImage.styleLockQualityPlaceholder')}
            onChange={(event) => updateStyleLock('quality', event.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label>{t('workbench.panelImage.styleLockNegative')}</Label>
          <Input
            value={config.styleLock.negative}
            disabled={disabled}
            placeholder={t('workbench.panelImage.styleLockNegativePlaceholder')}
            onChange={(event) => updateStyleLock('negative', event.target.value)}
          />
        </div>
      </div>
    </div>
  )
}

function OptionSelect({
  label,
  value,
  options,
  disabled,
  defaultLabel,
  onChange,
}: {
  label: string
  value: string
  options: string[]
  disabled: boolean
  defaultLabel: string
  onChange: (value: string) => void
}) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <Select
        value={value || SELECT_DEFAULT}
        disabled={disabled || options.length === 0}
        onValueChange={(next) => onChange(next === SELECT_DEFAULT ? '' : next)}
      >
        <SelectTrigger className="w-full">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={SELECT_DEFAULT}>{defaultLabel}</SelectItem>
          {options.map((option) => (
            <SelectItem key={option} value={option}>{option}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}

function PresetInput({
  label,
  value,
  options,
  placeholder,
  disabled,
  defaultLabel,
  onChange,
}: {
  label: string
  value: string
  options: string[]
  placeholder: string
  disabled: boolean
  defaultLabel: string
  onChange: (value: string) => void
}) {
  return (
    <div className="space-y-2">
      <OptionSelect
        label={label}
        value={options.includes(value) ? value : ''}
        options={options}
        disabled={disabled}
        defaultLabel={defaultLabel}
        onChange={onChange}
      />
      <Input value={value} disabled={disabled} placeholder={placeholder} onChange={(event) => onChange(event.target.value)} />
    </div>
  )
}
